'use client';

import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { lazy, Suspense } from 'react';

const LegalDocumentMarkdown = lazy(() => import('./legal-document-markdown'));

export type LegalDocumentContentProps = {
  /** Markdown, which may contain HTML. The HTML is sanitized before rendering. */
  text?: string;
  loading?: boolean;
  error?: boolean;
  errorText?: string;
  /** Shown with the error when retrying can help. */
  retryText?: string;
  onRetry?(): void;
};

function LegalDocumentSkeleton() {
  return (
    <Stack spacing={1}>
      {[100, 96, 100, 88, 100, 92, 60].map((width, index) => (
        <Skeleton key={index} variant="text" width={`${width}%`} />
      ))}
    </Stack>
  );
}

/** Body of a legal document, shared by the dialog and the standalone page. */
export function LegalDocumentContent({
  text,
  loading,
  error,
  errorText,
  retryText,
  onRetry,
}: LegalDocumentContentProps) {
  return (
    <>
      {loading && <LegalDocumentSkeleton />}
      {!loading && error && (
        <Stack spacing={2} sx={{ alignItems: 'center', py: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {errorText}
          </Typography>
          {onRetry && retryText && (
            <Button variant="outlined" onClick={onRetry}>
              {retryText}
            </Button>
          )}
        </Stack>
      )}
      {!loading && !error && text && (
        <Suspense fallback={<LegalDocumentSkeleton />}>
          <LegalDocumentMarkdown text={text} />
        </Suspense>
      )}
    </>
  );
}
